/**
 * TOSS Web Application - SignalR Connection Manager
 *
 * Singleton SignalR hub connection manager for real-time communication.
 * Features:
 * - Automatic connection management
 * - Authentication with JWT tokens
 * - Automatic reconnection with exponential backoff
 * - Connection state tracking
 * - Event subscription management
 *
 * Usage:
 * - Call startConnection() when user logs in
 * - Call stopConnection() when user logs out
 * - Use on() to subscribe to server events
 * - Use invoke() to call server methods
 */

import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  HttpTransportType,
} from '@microsoft/signalr';
import { TokenStorage } from '@/api/interceptors';
import { SIGNALR_HUBS } from '@/types';
import type { SignalRConnectionState } from '@/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Event handler type for SignalR events
 */
export type SignalREventHandler<T = unknown> = (data: T) => void;

/**
 * Connection state change handler
 */
export type ConnectionStateHandler = (state: SignalRConnectionState) => void;

/**
 * SignalR hub configuration
 */
export interface HubConfig {
  /** Hub URL path */
  url: string;
  /** Enable automatic reconnection */
  autoReconnect?: boolean;
  /** Logging level */
  logLevel?: LogLevel;
  /** Transport types to use (bitwise OR of HttpTransportType values) */
  transports?: HttpTransportType;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Default configuration for the notifications hub
 */
const DEFAULT_CONFIG: HubConfig = {
  url: SIGNALR_HUBS.NOTIFICATIONS,
  autoReconnect: true,
  logLevel: import.meta.env.DEV ? LogLevel.Information : LogLevel.Warning,
  transports: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
};

/**
 * Reconnection delays in milliseconds (exponential backoff)
 */
const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 30000];

// ============================================================================
// SignalR Manager Class
// ============================================================================

/**
 * SignalR connection manager class
 *
 * Manages the lifecycle of a SignalR hub connection including:
 * - Connection establishment and teardown
 * - Automatic reconnection with exponential backoff
 * - Event subscription management
 * - Authentication token injection
 */
class SignalRManager {
  private connection: HubConnection | null = null;
  private config: HubConfig;
  private stateHandlers: Set<ConnectionStateHandler> = new Set();
  private isConnecting = false;
  private reconnectAttempt = 0;
  private connectionPromise: Promise<void> | null = null;
  private connectionGeneration = 0;
  private manualReconnectTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Subscription registry — survives connection rebuild.
   * When a new HubConnection is created, all registered handlers are replayed onto it.
   */
  private subscriptions = new Map<string, Set<(...args: unknown[]) => void>>();

  constructor(config: HubConfig = DEFAULT_CONFIG) {
    this.config = config;
  }

  /**
   * Get current connection generation (for cleanup to capture)
   */
  get generation(): number {
    return this.connectionGeneration;
  }

  /**
   * Get current connection state
   */
  get state(): SignalRConnectionState {
    if (!this.connection) {
      return 'Disconnected';
    }

    switch (this.connection.state) {
      case HubConnectionState.Connected:
        return 'Connected';
      case HubConnectionState.Connecting:
        return 'Connecting';
      case HubConnectionState.Reconnecting:
        return 'Reconnecting';
      case HubConnectionState.Disconnecting:
        return 'Disconnecting';
      case HubConnectionState.Disconnected:
      default:
        return 'Disconnected';
    }
  }

  /**
   * Check if connection is established
   */
  get isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  /**
   * Get the underlying HubConnection (for advanced usage)
   */
  get hubConnection(): HubConnection | null {
    return this.connection;
  }

  /**
   * Build the SignalR connection
   */
  private buildConnection(): HubConnection {
    // Construct the full URL
    const baseUrl = import.meta.env.VITE_API_URL || '';
    const hubUrl = `${baseUrl}${this.config.url}`;

    const builder = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => TokenStorage.getAccessToken() || '',
        transport: this.config.transports,
      })
      .configureLogging(this.config.logLevel || LogLevel.Warning);

    // Timeouts mirror server-side HubOptions in ServiceCollectionExtensions.Infrastructure.cs:
    // ClientTimeoutInterval=60s, KeepAliveInterval=15s. Defaults (~30s/15s) caused
    // spurious OperationCanceledException in Seq every 20-30 min for idle tabs.
    const conn = builder
      .withServerTimeout(60_000)
      .withKeepAliveInterval(15_000);

    // Add automatic reconnection with custom delays
    if (this.config.autoReconnect) {
      conn.withAutomaticReconnect(RECONNECT_DELAYS);
    }

    return conn.build();
  }

  /**
   * Setup connection event handlers
   */
  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Connection closed — start manual reconnect if still authenticated
    this.connection.onclose((error) => {
      if (import.meta.env.DEV && error) {
        console.warn('[SignalR] Connection closed:', error);
      }
      this.notifyStateChange();

      if (TokenStorage.hasTokens()) {
        this.startManualReconnect();
      }
    });

    // Reconnecting
    this.connection.onreconnecting((error) => {
      if (import.meta.env.DEV && error) {
        console.warn('[SignalR] Reconnecting:', error);
      }
      this.reconnectAttempt++;
      this.notifyStateChange();
    });

    // Reconnected
    this.connection.onreconnected((connectionId) => {
      if (import.meta.env.DEV) {
        console.log('[SignalR] Reconnected:', connectionId);
      }
      this.reconnectAttempt = 0;
      this.notifyStateChange();
    });
  }

  /**
   * Notify all state change handlers
   */
  private notifyStateChange(): void {
    const currentState = this.state;
    this.stateHandlers.forEach((handler) => {
      try {
        handler(currentState);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[SignalR] State handler error:', error);
        }
      }
    });
  }

  /**
   * Replay all registered subscriptions onto the current connection.
   * Called after building a new HubConnection so handlers survive reconnection.
   */
  private replaySubscriptions(): void {
    if (!this.connection) return;
    for (const [eventName, handlers] of this.subscriptions) {
      for (const handler of handlers) {
        this.connection.on(eventName, handler);
      }
    }
  }

  /**
   * Start manual reconnection loop after built-in auto-reconnect is exhausted.
   * Retries every 10 seconds until connected or user logs out.
   */
  private startManualReconnect(): void {
    if (this.manualReconnectTimer) return;

    const MANUAL_RECONNECT_INTERVAL = 10_000;

    const attempt = async () => {
      if (this.isConnected || this.isConnecting || !TokenStorage.hasTokens()) {
        this.manualReconnectTimer = null;
        return;
      }

      try {
        // Null the old connection — doStartConnection will build a new one
        // and replay all subscriptions from the registry
        this.connection = null;
        await this.doStartConnection();
        this.manualReconnectTimer = null;
      } catch {
        this.manualReconnectTimer = setTimeout(attempt, MANUAL_RECONNECT_INTERVAL);
      }
    };

    this.manualReconnectTimer = setTimeout(attempt, MANUAL_RECONNECT_INTERVAL);
  }

  /**
   * Start the connection
   *
   * @returns Promise that resolves when connected
   */
  async startConnection(): Promise<void> {
    // Don't start if already connected
    if (this.isConnected) {
      return;
    }

    // If already connecting, return the existing promise
    if (this.isConnecting && this.connectionPromise) {
      return this.connectionPromise;
    }

    // Check for authentication token
    if (!TokenStorage.hasTokens()) {
      if (import.meta.env.DEV) {
        console.warn('[SignalR] No auth tokens available, skipping connection');
      }
      return;
    }

    // Increment generation for this connection attempt
    // This is used to detect stale disconnect requests from React StrictMode
    this.connectionGeneration++;

    this.isConnecting = true;
    this.notifyStateChange();

    this.connectionPromise = this.doStartConnection();
    return this.connectionPromise;
  }

  /**
   * Internal method to perform the actual connection
   */
  private async doStartConnection(): Promise<void> {
    try {
      // Build new connection if needed
      if (!this.connection) {
        this.connection = this.buildConnection();
        this.setupEventHandlers();
        this.replaySubscriptions();
      }

      // Start the connection
      await this.connection.start();
      this.reconnectAttempt = 0;

      if (import.meta.env.DEV) {
        console.log('[SignalR] Connected to', this.config.url);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[SignalR] Connection failed:', error);
      }
      throw error;
    } finally {
      this.isConnecting = false;
      this.connectionPromise = null;
      this.notifyStateChange();
    }
  }

  /**
   * Stop the connection
   *
   * @param expectedGeneration - Optional generation number to check before disconnecting.
   *                             If provided and doesn't match current generation, the disconnect
   *                             is skipped. This prevents React StrictMode's stale cleanup from
   *                             killing newer connections.
   */
  async stopConnection(expectedGeneration?: number): Promise<void> {
    // If generation provided and doesn't match, skip disconnect
    // This prevents stale cleanup from disconnecting newer connections
    if (expectedGeneration !== undefined && expectedGeneration !== this.connectionGeneration) {
      if (import.meta.env.DEV) {
        console.log('[SignalR] Ignoring stale disconnect request (generation mismatch)');
      }
      return;
    }

    // Cancel any pending manual reconnect
    if (this.manualReconnectTimer) {
      clearTimeout(this.manualReconnectTimer);
      this.manualReconnectTimer = null;
    }

    // Wait for any pending connection attempt to complete first
    // This prevents "stopped during negotiation" errors
    if (this.connectionPromise) {
      try {
        await this.connectionPromise;
      } catch {
        // Ignore errors - we just want to wait for the attempt to finish
      }
    }

    if (!this.connection) {
      return;
    }

    try {
      await this.connection.stop();
      if (import.meta.env.DEV) {
        console.log('[SignalR] Disconnected');
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[SignalR] Error stopping connection:', error);
      }
    } finally {
      this.connection = null;
      this.subscriptions.clear();
      this.notifyStateChange();
    }
  }

  /**
   * Force a fresh handshake on the SAME underlying connection, without touching the
   * subscription registry or any handler registered through {@link on}.
   *
   * Unlike {@link stopConnection} (used at logout — deliberately tears the connection down
   * and clears everything), this is for "the server needs to see me connect again": e.g.
   * `MessengerHub.OnConnectedAsync` resolves an external caller's chat groups ONCE, at
   * connect time, and SignalR groups are sticky — the customer portal's company switcher
   * (client-portal Р4, decision Р20) needs the hub to re-run that resolution under the
   * NEW active company. `stopConnection()` would be the wrong tool here: it nulls
   * `this.connection` and clears `this.subscriptions`, so every caller's `.on()` handler
   * would go silently unregistered — and nothing would ever re-register them, because
   * nothing in a caller's own `useEffect` deps changes just because this method ran.
   *
   * A `HubConnection` instance is reusable: `.on()` registrations live on the instance
   * itself, not on the underlying transport, so stopping and starting the SAME instance
   * keeps every handler intact — no replay needed.
   *
   * A no-op when there is nothing to reconnect (never connected this session) — the next
   * `startConnection()` call connects fresh under whatever is active by then anyway.
   */
  async reconnect(): Promise<void> {
    if (!this.connection) {
      return;
    }

    if (this.manualReconnectTimer) {
      clearTimeout(this.manualReconnectTimer);
      this.manualReconnectTimer = null;
    }

    // Wait for any pending connection attempt first — same reasoning as stopConnection().
    if (this.connectionPromise) {
      try {
        await this.connectionPromise;
      } catch {
        // Ignore — only waiting for the in-flight attempt to settle.
      }
    }

    this.connectionGeneration++;
    this.isConnecting = true;
    this.notifyStateChange();

    try {
      try {
        await this.connection.stop();
      } catch {
        // Already stopped, or stopping failed — start() below still re-attempts.
      }
      await this.connection.start();
      this.reconnectAttempt = 0;
      if (import.meta.env.DEV) {
        console.log('[SignalR] Reconnected (forced) to', this.config.url);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[SignalR] Forced reconnect failed:', error);
      }
      throw error;
    } finally {
      this.isConnecting = false;
      this.notifyStateChange();
    }
  }

  /**
   * Subscribe to a server event
   *
   * @param eventName - Name of the event to subscribe to
   * @param handler - Handler function for the event
   * @returns Unsubscribe function
   *
   * @example
   * ```ts
   * const unsubscribe = signalR.on('ReceiveNotification', (notification) => {
   *   console.log('New notification:', notification);
   * });
   *
   * // Later, to unsubscribe:
   * unsubscribe();
   * ```
   */
  on<T = unknown>(eventName: string, handler: SignalREventHandler<T>): () => void {
    const typedHandler = handler as (...args: unknown[]) => void;

    // Track in subscription registry (survives connection rebuild)
    if (!this.subscriptions.has(eventName)) {
      this.subscriptions.set(eventName, new Set());
    }
    this.subscriptions.get(eventName)!.add(typedHandler);

    // Register on current connection if available
    this.connection?.on(eventName, typedHandler);

    // Return unsubscribe function
    return () => {
      this.subscriptions.get(eventName)?.delete(typedHandler);
      this.connection?.off(eventName, typedHandler);
    };
  }

  /**
   * Unsubscribe from a server event
   *
   * @param eventName - Name of the event
   * @param handler - Handler to remove (optional, removes all if not provided)
   */
  off<T = unknown>(eventName: string, handler?: SignalREventHandler<T>): void {
    if (handler) {
      const typedHandler = handler as (...args: unknown[]) => void;
      this.subscriptions.get(eventName)?.delete(typedHandler);
      this.connection?.off(eventName, typedHandler);
    } else {
      this.subscriptions.delete(eventName);
      this.connection?.off(eventName);
    }
  }

  /**
   * Invoke a method on the server
   *
   * @param methodName - Name of the server method
   * @param args - Arguments to pass to the method
   * @returns Promise with the result
   *
   * @example
   * ```ts
   * await signalR.invoke('JoinGroup', 'user-123');
   * ```
   */
  async invoke<T = void>(methodName: string, ...args: unknown[]): Promise<T> {
    if (!this.connection || !this.isConnected) {
      throw new Error('SignalR connection is not established');
    }

    return this.connection.invoke<T>(methodName, ...args);
  }

  /**
   * Send a message to the server (fire-and-forget)
   *
   * @param methodName - Name of the server method
   * @param args - Arguments to pass to the method
   */
  async send(methodName: string, ...args: unknown[]): Promise<void> {
    if (!this.connection || !this.isConnected) {
      throw new Error('SignalR connection is not established');
    }

    return this.connection.send(methodName, ...args);
  }

  /**
   * Subscribe to connection state changes
   *
   * @param handler - Handler function for state changes
   * @returns Unsubscribe function
   *
   * @example
   * ```ts
   * const unsubscribe = signalR.onStateChange((state) => {
   *   console.log('Connection state:', state);
   * });
   * ```
   */
  onStateChange(handler: ConnectionStateHandler): () => void {
    this.stateHandlers.add(handler);

    // Immediately notify of current state
    handler(this.state);

    // Return unsubscribe function
    return () => {
      this.stateHandlers.delete(handler);
    };
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

/**
 * Singleton SignalR manager instance for the notifications hub
 *
 * @example
 * ```ts
 * import { signalRManager } from '@/realtime/signalr';
 *
 * // Start connection on login
 * await signalRManager.startConnection();
 *
 * // Subscribe to events
 * signalRManager.on('ReceiveNotification', handleNotification);
 *
 * // Stop connection on logout
 * await signalRManager.stopConnection();
 * ```
 */
export const signalRManager = new SignalRManager();

/**
 * Export the SignalRManager class for creating additional hub connections
 */
export { SignalRManager };

export default signalRManager;
